import { useState } from "react";

import { downloadReport } from "../api/reports";
import type { CityReportData } from "../types";

function formatInt(n: number | undefined | null): string {
  return typeof n === "number" ? n.toLocaleString("en-US") : "—";
}

function growthLabel(growth: number | null | undefined, previousMonth?: string): string | null {
  if (growth === null || growth === undefined) return null;
  const suffix = previousMonth ? ` vs ${previousMonth}` : " vs last month";
  const sign = growth >= 0 ? "+" : "";
  return `${sign}${growth.toFixed(1)}%${suffix}`;
}

// "+791 (+4.2%) vs July": absolute change in the running total plus the percentage.
function growthDelta(
  current: number,
  previous: number | null | undefined,
  growth: number | null | undefined,
  previousMonth?: string
): string | null {
  const pct = growthLabel(growth, previousMonth);
  if (previous === null || previous === undefined) return pct;
  const diff = current - previous;
  const sign = diff >= 0 ? "+" : "";
  const suffix = previousMonth ? ` vs ${previousMonth}` : " vs last month";
  return pct ? `${sign}${formatInt(diff)} (${pct.replace(suffix, "")})${suffix}` : `${sign}${formatInt(diff)}${suffix}`;
}

function Metric({
  label,
  value,
  delta,
  sub,
}: {
  label: string;
  value: string;
  delta?: string | null;
  sub?: string | null;
}) {
  const deltaClass = delta && delta.trim().startsWith("+") ? "metric-delta up" : "metric-delta down";
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-value">{value}</div>
      {sub && <div className="metric-sub">{sub}</div>}
      {delta && <div className={deltaClass}>{delta}</div>}
    </div>
  );
}

// Rendered separately from the card so it can sit after the LLM summary text.
export function DownloadReportButton({ data }: { data: CityReportData }) {
  // The PDF report is a per-location report; totals have none.
  if (data?.scope) return null;
  return <CityDownloadButton data={data} />;
}

function CityDownloadButton({ data }: { data: CityReportData }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function handleDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadReport(data);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : "Unable to generate the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="report-download">
      <button className="btn btn-secondary" onClick={handleDownload} disabled={downloading}>
        {downloading ? "Preparing PDF…" : "Download PDF Report"}
      </button>
      {downloadError && <div className="alert alert-error">{downloadError}</div>}
    </div>
  );
}

function TotalsCard({ data }: { data: CityReportData }) {
  const title = data.scope === "india" ? "India" : data.state ?? "State";
  const monthShort = data.month ? data.month.slice(0, 3) : null;
  const prevShort = data.previous_month ? data.previous_month.slice(0, 3) : null;
  const prevSub = (prev: number | null | undefined, prefix: string) =>
    prev !== null && prev !== undefined && prevShort ? `${prefix} ${prevShort}: ${formatInt(prev)}` : null;

  if (data.status !== "success" || data.total_evs === undefined) {
    return (
      <div className="data-card">
        <h4 className="data-card-title">{title}</h4>
        <div className="alert alert-info">{data.message ?? data.error ?? "No data available."}</div>
      </div>
    );
  }

  return (
    <div className="data-card">
      <h4 className="data-card-title">{title}</h4>
      <div className="muted-caption">
        Total EVs registered till end of {data.month} {data.year} (Cars + Buses + Trucks)
      </div>

      <div className="metrics-row">
        <Metric
          label={`Total EVs${monthShort ? ` (till ${monthShort})` : ""}`}
          value={formatInt(data.total_evs)}
          sub={prevSub(data.previous_total_evs, "Till")}
          delta={growthDelta(data.total_evs, data.previous_total_evs, data.growth_percent, data.previous_month)}
        />
        {data.monthly_evs != null && (
          <Metric
            label={`New EVs in ${monthShort}`}
            value={formatInt(data.monthly_evs)}
            sub={prevSub(data.previous_monthly_evs, "Sold in")}
            delta={growthDelta(
              data.monthly_evs,
              data.previous_monthly_evs,
              data.monthly_growth_percent,
              data.previous_month
            )}
          />
        )}
      </div>

      {data.breakdown && (
        <>
          <div className="section-caption">Breakdown{monthShort ? ` (till ${monthShort})` : ""}</div>
          <div className="metrics-row metrics-row-3">
            <Metric label="Cars" value={formatInt(data.breakdown.Cars ?? 0)} />
            <Metric label="Buses" value={formatInt(data.breakdown.Buses ?? 0)} />
            <Metric label="Trucks" value={formatInt(data.breakdown.Trucks ?? 0)} />
          </div>
          {data.rto_count != null && <div className="muted-caption">Across {formatInt(data.rto_count)} RTO(s)</div>}
        </>
      )}
    </div>
  );
}

export function DataCard({ data }: { data: CityReportData }) {
  if (data?.scope) return <TotalsCard data={data} />;
  return <CityCard data={data} />;
}

function CityCard({ data }: { data: CityReportData }) {
  const [stationsOpen, setStationsOpen] = useState(true);
  const [rtosOpen, setRtosOpen] = useState(false);

  if (!data) return null;

  const local = data.local ?? null;
  const radiusRegs = data.radius_registrations ?? [];
  const localStations = data.local_stations ?? [];
  const radiusStations = data.radius_stations ?? [];

  const localTotal = local ? local.total_evs : 0;
  const radiusTotal = radiusRegs.reduce((sum, r) => sum + (r.total_evs || 0), 0);

  const rawTitle = data.city ?? (data.coords ? `(${data.coords[0].toFixed(3)}, ${data.coords[1].toFixed(3)})` : "Location");
  const title = rawTitle.charAt(0).toUpperCase() + rawTitle.slice(1);

  const allStations = [...localStations, ...radiusStations];

  // EV counts are cumulative totals up to the end of `month`; the backend also
  // sends the total as of the month before so we can show the growth.
  const monthShort = data.month ? data.month.slice(0, 3) : null;
  const prevShort = data.previous_month ? data.previous_month.slice(0, 3) : null;
  const tillLabel = monthShort ? ` (till ${monthShort})` : "";
  const prevSub = (prev: number | null | undefined, prefix = "Till") =>
    prev !== null && prev !== undefined && prevShort ? `${prefix} ${prevShort}: ${formatInt(prev)}` : null;

  return (
    <div className="data-card">
      <h4 className="data-card-title">{title}</h4>
      {data.month && data.year && (
        <div className="muted-caption">
          Total EVs registered till end of {data.month} {data.year} (Cars + Buses + Trucks)
        </div>
      )}

      <div className="metrics-row">
        <Metric
          label={`Local Total${tillLabel}`}
          value={local ? formatInt(localTotal) : "—"}
          sub={prevSub(data.previous_local_total_evs)}
          delta={growthDelta(localTotal, data.previous_local_total_evs, data.local_growth_percent, data.previous_month)}
        />
        <Metric
          label={`Within ${(data.radius_km ?? 100).toFixed(0)}km${tillLabel}`}
          value={formatInt(radiusTotal)}
          sub={prevSub(data.previous_radius_total_evs)}
          delta={growthDelta(radiusTotal, data.previous_radius_total_evs, data.radius_growth_percent, data.previous_month)}
        />
      </div>

      {data.monthly_local_evs != null && (
        <>
          <div className="section-caption">New EVs registered in {data.month}</div>
          <div className="metrics-row">
            <Metric
              label={`Local sales (${monthShort})`}
              value={formatInt(data.monthly_local_evs)}
              sub={prevSub(data.previous_monthly_local_evs, "Sold in")}
              delta={growthDelta(
                data.monthly_local_evs,
                data.previous_monthly_local_evs,
                data.monthly_local_growth_percent,
                data.previous_month
              )}
            />
            <Metric
              label={`Within ${(data.radius_km ?? 100).toFixed(0)}km sales (${monthShort})`}
              value={formatInt(data.monthly_radius_evs)}
              sub={prevSub(data.previous_monthly_radius_evs, "Sold in")}
              delta={growthDelta(
                data.monthly_radius_evs ?? 0,
                data.previous_monthly_radius_evs,
                data.monthly_radius_growth_percent,
                data.previous_month
              )}
            />
          </div>
        </>
      )}

      {local && (
        <>
          <div className="section-caption">Local breakdown{tillLabel}</div>
          <div className="metrics-row metrics-row-3">
            <Metric label="Cars" value={formatInt(local.breakdown?.Cars ?? 0)} />
            <Metric label="Buses" value={formatInt(local.breakdown?.Buses ?? 0)} />
            <Metric label="Trucks" value={formatInt(local.breakdown?.Trucks ?? 0)} />
          </div>
          <div className="muted-caption">
            Combined from {local.matched_rtos?.length ?? 0} RTO(s): {(local.matched_rtos ?? []).join(", ")}
          </div>
        </>
      )}

      {radiusRegs.length > 0 && (
        <details className="expander" open={rtosOpen} onToggle={(e) => setRtosOpen(e.currentTarget.open)}>
          <summary>Nearby RTOs ({radiusRegs.length})</summary>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>RTO</th>
                  <th>Cars</th>
                  <th>Buses</th>
                  <th>Trucks</th>
                  <th>Total EVs{tillLabel}</th>
                  <th>Distance (km)</th>
                </tr>
              </thead>
              <tbody>
                {radiusRegs.slice(0, 20).map((r, i) => (
                  <tr key={`${r.rto_name}_${i}`}>
                    <td>{r.rto_name}</td>
                    <td>{formatInt(r.Cars)}</td>
                    <td>{formatInt(r.Buses)}</td>
                    <td>{formatInt(r.Trucks)}</td>
                    <td>{formatInt(r.total_evs)}</td>
                    <td>{r.distance_km.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {allStations.length > 0 ? (
        <details className="expander" open={stationsOpen} onToggle={(e) => setStationsOpen(e.currentTarget.open)}>
          <summary>Charging stations ({allStations.length})</summary>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Station</th>
                  <th>Highway</th>
                  <th>CUF</th>
                  <th>TUF</th>
                </tr>
              </thead>
              <tbody>
                {allStations.map((s, i) => {
                  const highway = [s.nh, s.sh].filter(Boolean).join(" · ") || "No highway match";
                  const cuf = s.cuf_installed_percent;
                  const tuf = s.tuf_installed_percent;

                  return (
                    <tr key={`${s.station_name ?? "station"}_${i}`}>
                      <td>{s.station_name ?? "Unknown"}</td>
                      <td>{highway}</td>
                      <td>{cuf != null ? `${Number(cuf).toFixed(2)}` : "—"}</td>
                      <td>{tuf != null ? `${Number(tuf).toFixed(2)}` : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      ) : (
        <div className="alert alert-info">No charging station data available for this location.</div>
      )}
    </div>
  );
}
